import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, Copy, Eye, Inbox, Link2, Send, Trash2 } from 'lucide-react';
import { internalApi } from '../lib/hubApi';
import { ConfirmModal } from '../components/shared/ConfirmModal';
import { DeleteRecordDialog } from '../components/shared/DeleteRecordDialog';
import {
  confirmWordFor,
  deleteRecordNotice,
  type DeletedLead,
  type DeleteOutcome,
} from '../lib/deleteRecord';

type LeadStatus = 'invited' | 'connected' | 'unlocked';

type SnapshotSummary = {
  orgId: string;
  orgName: string;
  orgType: 'production' | 'sandbox';
  capturedAt: number;
  healthTotal: number | null;
  findingsCount: number;
};

// Mirrors ScanInviteState in lead.service.ts.
type InviteState = {
  status: 'active' | 'used' | 'expired';
  expiresAt: number;
  consumedAt: number | null;
};

type InviteInput = { name: string; email: string; company: string };

type LeadRow = {
  id: string;
  name: string;
  email: string;
  company: string;
  status: LeadStatus;
  createdAt: number;
  updatedAt: number;
  // Non-null = this client approved a scan, so a delete keeps the approval
  // record and leaves a tombstone. Null = the whole record goes.
  consentAt: number | null;
  // Last click on the teaser's Balo booking link (TYR-227).
  baloClickedAt: number | null;
  snapshot: SnapshotSummary | null;
  invite: InviteState | null;
};

// Funnel order — drives both the stage-column display and the "is this
// action still available?" gating (forward-only).
const STAGES: { key: LeadStatus; label: string; style: string }[] = [
  { key: 'invited', label: 'Invited', style: 'bg-surface-3 text-neutral-400 border-border' },
  { key: 'connected', label: 'Connected', style: 'bg-design/15 text-design border-design/40' },
  { key: 'unlocked', label: 'Unlocked', style: 'bg-grow/15 text-grow border-grow/40' },
];
// Derived from STAGES so the order lives in exactly one place.
const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i])) as Record<
  LeadStatus,
  number
>;

function StageBadge({ status }: { status: LeadStatus }) {
  const s = STAGES.find((x) => x.key === status) ?? STAGES[0];
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${s.style}`}>
      {s.label}
    </span>
  );
}

function healthColor(n: number): string {
  return n >= 70 ? '#3FA96B' : n >= 50 ? '#C7851A' : '#D64545';
}

function relative(ts: number): string {
  const d = Date.now() - ts;
  const day = 86_400_000;
  if (d < 3_600_000) return `${Math.max(1, Math.round(d / 60_000))}m ago`;
  if (d < day) return `${Math.round(d / 3_600_000)}h ago`;
  return `${Math.round(d / day)}d ago`;
}

// Invite links live 24h, so hours and minutes are the only units needed.
function timeLeft(expiresAt: number): string {
  const ms = expiresAt - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.round(ms / 60_000))}m left`;
}

export function LeadsPage() {
  const location = useLocation();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pending, setPending] = useState<{ kind: 'hub-invite'; lead: LeadRow } | null>(null);
  // Delete (TYR-219): the record awaiting confirmation, the inline error from
  // the last attempt, and the plain line shown once it is done. The line can
  // also arrive from the scan page, which deletes and then lands here.
  const [toDelete, setToDelete] = useState<LeadRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { deletedNotice?: string } | null)?.deletedNotice ?? null,
  );
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedRows, setDeletedRows] = useState<DeletedLead[] | null>(null);
  // The most recent invite link, shown until the consultant creates another.
  // It is displayed exactly once per creation: the token is not readable back
  // from the server, so this panel is the only place to copy it from.
  const [lastLink, setLastLink] = useState<{ link: string; label: string } | null>(null);
  // Set when the server answers 409: this lead still holds a live link, so ask
  // before taking it down. Shared by the form and the row button.
  const [replaceAsk, setReplaceAsk] = useState<{
    input: InviteInput;
    label: string;
    message: string;
    onDone?: () => void;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await internalApi.get<{ leads: LeadRow[] }>('/leads');
      setLeads(data.leads);
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(m ?? (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    const c: Record<LeadStatus, number> = { invited: 0, connected: 0, unlocked: 0 };
    for (const l of leads) c[l.status]++;
    return c;
  }, [leads]);

  // Post the invite. `replace` is the explicit answer to the server's 409, so
  // it is never sent on the first try: whether the lead still holds a live
  // link is the server's call, not a row this page cached before another tab
  // or a retry changed it.
  const postInvite = useCallback(
    async (input: InviteInput, label: string, replace: boolean) => {
      const { data } = await internalApi.post<{ link: string }>('/scan-invite', {
        ...input,
        ...(replace ? { replace: true } : {}),
      });
      setLastLink({ link: data.link, label });
      await load();
    },
    [load],
  );

  // One path for every invite, from the form and from a row. A dead link comes
  // straight back as 200, so the extra step only appears when the server says
  // a live link is about to be taken down.
  const requestInvite = useCallback(
    async (input: InviteInput, label: string, onDone?: () => void) => {
      try {
        await postInvite(input, label, false);
        toast.success('Invite link ready. Copy it into your email.');
        onDone?.();
      } catch (e) {
        const res = (e as {
          response?: { status?: number; data?: { error?: string; message?: string } };
        }).response;
        if (res?.status === 409) {
          setReplaceAsk({
            input,
            label,
            onDone,
            message:
              res.data?.message ??
              `${input.email} already has an active link. A new link stops the old one from working.`,
          });
          return;
        }
        toast.error(res?.data?.message ?? res?.data?.error ?? (e as Error).message);
      }
    },
    [postInvite],
  );

  const newLink = async (lead: LeadRow) => {
    setBusyId(lead.id);
    try {
      await requestInvite(
        { name: lead.name, email: lead.email, company: lead.company },
        lead.company,
      );
    } finally {
      setBusyId(null);
    }
  };

  const loadDeleted = useCallback(async () => {
    try {
      const { data } = await internalApi.get<{ deleted: DeletedLead[] }>('/leads/deleted');
      setDeletedRows(data.deleted);
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (e as Error).message);
    }
  }, []);

  // The delete itself. The dialog stays open on failure, with the reason in
  // it; on success the list reloads without the row and the notice takes over.
  const deleteRecord = async (lead: LeadRow) => {
    setBusyId(lead.id);
    setDeleteError(null);
    try {
      const { data } = await internalApi.delete<{ outcome: DeleteOutcome }>(`/leads/${lead.id}`);
      setToDelete(null);
      setNotice(deleteRecordNotice(data.outcome));
      if (showDeleted) await loadDeleted();
      await load();
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setDeleteError(m ?? (e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleDeleted = async () => {
    const next = !showDeleted;
    setShowDeleted(next);
    if (next) await loadDeleted();
  };

  const hubInvite = async (lead: LeadRow) => {
    if (!lead.snapshot) return;
    setBusyId(lead.id);
    try {
      await internalApi.post('/invite', {
        email: lead.email,
        sfOrgId: lead.snapshot.orgId,
        orgType: lead.snapshot.orgType,
      });
      toast.success(`Hub invite sent to ${lead.email}`);
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      toast.error(m ?? (e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center gap-2.5 mb-1">
        <Inbox size={20} className="text-grow" />
        <h1 className="text-2xl font-semibold text-crux-light">Client scans</h1>
      </div>
      <p className="text-sm text-neutral-400 mb-6">
        Create an invite link, then paste it into your own email. OrgIQ sends nothing. The link is
        valid for 24 hours and allows one scan. Open a connected client to screen-share its audit
        snapshot, then invite them to the Hub.
      </p>

      {notice ? (
        <p className="mb-4 border border-border bg-surface-2 px-3 py-2 text-sm text-neutral-300">
          {notice}
        </p>
      ) : null}

      <InviteForm onSubmit={requestInvite} />
      {lastLink ? <InviteLink link={lastLink.link} label={lastLink.label} /> : null}

      {/* Stage counts */}
      <div className="flex flex-wrap gap-2 mb-6 mt-8">
        {STAGES.map((s) => (
          <div key={s.key} className="bg-surface-2 border border-border px-3 py-2">
            <div className="text-lg font-semibold text-crux-light tabular-nums leading-none">
              {counts[s.key]}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Loading clients…</p>
      ) : error ? (
        <p className="text-sm text-sunset">{error}</p>
      ) : leads.length === 0 ? (
        <p className="text-sm text-neutral-500">No clients yet. Create the first invite above.</p>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-neutral-500 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left py-2.5 px-4">Client</th>
                <th className="text-left py-2.5 px-4">Stage</th>
                <th className="text-left py-2.5 px-4">Invite</th>
                <th className="text-left py-2.5 px-4">Org</th>
                <th className="text-right py-2.5 px-4">Health</th>
                <th className="text-left py-2.5 px-4">Captured</th>
                <th className="text-right py-2.5 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const snap = lead.snapshot;
                const canHubInvite = Boolean(snap) && STAGE_INDEX[lead.status] < STAGE_INDEX.unlocked;
                // An active link is already in the client's hands; re-issuing
                // it would break that one. Offer a new link once it is spent,
                // expired, or was never created (leads from before TYR-204).
                const canReissue = lead.invite?.status !== 'active';
                return (
                  <tr key={lead.id} className="border-t border-border hover:bg-surface-2/50">
                    <td className="py-2.5 px-4">
                      <div className="text-crux-light font-medium">{lead.name}</div>
                      <div className="text-xs text-neutral-500">
                        {lead.company} · {lead.email}
                      </div>
                      {lead.baloClickedAt ? (
                        <div className="text-xs text-grow mt-0.5">
                          Booking link clicked {new Date(lead.baloClickedAt).toLocaleDateString()}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2.5 px-4">
                      <StageBadge status={lead.status} />
                    </td>
                    <td className="py-2.5 px-4">
                      <InviteCell invite={lead.invite} />
                    </td>
                    <td className="py-2.5 px-4">
                      {snap ? (
                        <div>
                          <span className="text-crux-light">{snap.orgName}</span>
                          {snap.orgType === 'sandbox' ? (
                            <span className="ml-1.5 text-[10px] text-sunrise">sandbox</span>
                          ) : null}
                          <div className="text-xs text-neutral-500">{snap.findingsCount} findings</div>
                        </div>
                      ) : (
                        <span className="text-neutral-600">-</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums">
                      {snap?.healthTotal != null ? (
                        <span style={{ color: healthColor(snap.healthTotal) }} className="font-semibold">
                          {snap.healthTotal}
                        </span>
                      ) : (
                        <span className="text-neutral-600">-</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-neutral-400">
                      {snap ? relative(snap.capturedAt) : '-'}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {snap ? (
                          <Link
                            to={`/internal/leads/${lead.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-border text-neutral-300 hover:text-crux-light hover:border-border-strong"
                          >
                            <Eye size={13} />
                            Snapshot
                          </Link>
                        ) : null}
                        {canReissue ? (
                          <button
                            type="button"
                            disabled={busyId === lead.id}
                            onClick={() => void newLink(lead)}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-border text-neutral-300 hover:text-crux-light hover:border-border-strong disabled:opacity-50"
                          >
                            <Link2 size={13} />
                            New link
                          </button>
                        ) : null}
                        {canHubInvite ? (
                          <button
                            type="button"
                            disabled={busyId === lead.id}
                            onClick={() => setPending({ kind: 'hub-invite', lead })}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-grow/40 text-grow hover:bg-grow/10 disabled:opacity-50"
                          >
                            <Send size={13} />
                            Hub invite
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={() => {
                            setDeleteError(null);
                            setToDelete(lead);
                          }}
                          aria-label={`Delete the scan record for ${lead.company}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-border text-neutral-400 hover:text-sunset hover:border-sunset/40 disabled:opacity-50"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Deleted records. Hidden by default: a tombstone is evidence, not a
          client. It carries the approval record and the deleted date, which
          is everything that survived the delete. */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => void toggleDeleted()}
          className="text-xs text-neutral-500 hover:text-crux-light underline underline-offset-2"
        >
          {showDeleted ? 'Hide deleted' : 'Show deleted'}
        </button>
        {showDeleted ? <DeletedTable rows={deletedRows} /> : null}
      </div>

      <DeleteRecordDialog
        open={toDelete !== null}
        client={toDelete ? toDelete.company || toDelete.name : ''}
        confirmWord={toDelete ? confirmWordFor(toDelete) : 'delete'}
        approved={toDelete?.consentAt != null}
        busy={busyId === toDelete?.id}
        error={deleteError}
        onConfirm={() => {
          if (toDelete) void deleteRecord(toDelete);
        }}
        onCancel={() => setToDelete(null)}
      />

      <ConfirmModal
        open={pending?.kind === 'hub-invite'}
        title="Send Hub invite?"
        body={`Email ${pending?.lead.email ?? ''} a 14-day activation link for their org's Hub. On activation the client reaches Unlocked.`}
        confirmLabel="Send invite"
        onConfirm={async () => {
          if (pending) await hubInvite(pending.lead);
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      />

      {/* Only reached when the server refused to replace a live link. */}
      <ConfirmModal
        open={replaceAsk !== null}
        title="Replace the active link?"
        body={replaceAsk?.message ?? ''}
        confirmLabel="Create a new link"
        onConfirm={async () => {
          const ask = replaceAsk;
          setReplaceAsk(null);
          if (!ask) return;
          try {
            await postInvite(ask.input, ask.label, true);
            toast.success('New link ready. Copy it into your email.');
            ask.onDone?.();
          } catch (e) {
            const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
            toast.error(m ?? (e as Error).message);
          }
        }}
        onCancel={() => setReplaceAsk(null)}
      />
    </div>
  );
}

// The approval record of a deleted scan, and when it was deleted. Nothing
// else is left to show: no name, no company, no results.
function DeletedTable({ rows }: { rows: DeletedLead[] | null }) {
  if (rows === null) return <p className="mt-3 text-sm text-neutral-500">Loading deleted records…</p>;
  if (rows.length === 0) {
    return <p className="mt-3 text-sm text-neutral-500">No deleted records.</p>;
  }
  const stamp = (ts: number | null): string => (ts ? new Date(ts).toLocaleString() : '-');
  return (
    <div className="mt-3 border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2 text-neutral-500 text-[11px] uppercase tracking-wide">
          <tr>
            <th className="text-left py-2.5 px-4">Invited email</th>
            <th className="text-left py-2.5 px-4">Terms</th>
            <th className="text-left py-2.5 px-4">Approved</th>
            <th className="text-left py-2.5 px-4">Approver</th>
            <th className="text-left py-2.5 px-4">Deleted</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="py-2.5 px-4 text-neutral-300">{r.email}</td>
              <td className="py-2.5 px-4 text-neutral-400">{r.consentVersion ?? '-'}</td>
              <td className="py-2.5 px-4 text-neutral-400">
                {stamp(r.consentAt)}
                <div className="text-xs text-neutral-500">{r.consentIp ?? 'no IP recorded'}</div>
              </td>
              <td className="py-2.5 px-4 text-neutral-400">
                {r.approvedByUsername ?? 'not completed'}
                <div className="text-xs text-neutral-500">{r.approvedByOrgId ?? '-'}</div>
              </td>
              <td className="py-2.5 px-4 text-neutral-400">{stamp(r.deletedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Invite state per row: how long the link has left, or why it no longer works.
function InviteCell({ invite }: { invite: InviteState | null }) {
  if (!invite) return <span className="text-neutral-600 text-xs">no link</span>;
  if (invite.status === 'active') {
    return (
      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border bg-adopt/15 text-adopt border-adopt/40">
        {timeLeft(invite.expiresAt)}
      </span>
    );
  }
  const label = invite.status === 'used' ? 'used' : 'expired';
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border bg-surface-3 text-neutral-500 border-border">
      {label}
    </span>
  );
}

// The 409 handling and its confirm step live in the page, because the row
// buttons need the same path. This form only collects the fields and clears
// them once an invite really was created, which may be after the confirm.
function InviteForm({
  onSubmit,
}: {
  onSubmit: (input: InviteInput, label: string, onDone?: () => void) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = { name, email, company };
    setBusy(true);
    try {
      await onSubmit(input, company, () => {
        setName('');
        setEmail('');
        setCompany('');
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-surface-2 border border-border p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-3">
        New scan invite
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Contact name"
          className="h-9 bg-surface-3 border border-border px-3 text-[13px] text-crux-light placeholder:text-neutral-500 outline-none focus:border-adopt"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Work email"
          className="h-9 bg-surface-3 border border-border px-3 text-[13px] text-crux-light placeholder:text-neutral-500 outline-none focus:border-adopt"
        />
        <input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          required
          placeholder="Company"
          className="h-9 bg-surface-3 border border-border px-3 text-[13px] text-crux-light placeholder:text-neutral-500 outline-none focus:border-adopt"
        />
        <button
          type="submit"
          disabled={busy}
          className="h-9 px-4 bg-sfblue hover:opacity-90 text-white font-semibold text-[13px] disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create link'}
        </button>
      </div>
    </form>
  );
}

// The link is shown once, right after it is created. Copy is the whole point
// of this panel: OrgIQ never emails it.
function InviteLink({ link, label }: { link: string; label: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard is blocked outside a secure context; the field stays
      // selectable so the link is never trapped in the page.
      toast.error('Copy blocked by the browser. Select the link and copy it.');
    }
  };

  return (
    <div className="mt-3 border border-adopt/40 bg-adopt/5 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-adopt mb-2">
        Invite link for {label} · valid 24 hours · one scan
      </div>
      <div className="flex gap-2">
        <input
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 h-9 bg-surface-3 border border-border px-3 text-[12px] text-crux-light outline-none"
        />
        <button
          type="button"
          onClick={() => void copy()}
          className="h-9 px-3 inline-flex items-center gap-1.5 border border-border text-neutral-300 text-[13px] hover:text-crux-light hover:border-border-strong"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-[11px] text-neutral-500 mt-2">
        This is the only time the link is shown. Create a new one if you lose it.
      </p>
    </div>
  );
}
